import { useQuery } from "@tanstack/react-query";
import { Search, UserPlus, X } from "lucide-react";
import { useNotify, type Identifier } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { CLIENT_STAGES, labelOf } from "../gulaConstants";
import { searchTerm } from "./social";

interface LeadOption {
  contact_id: number;
  name: string | null;
  phone: string | null;
  stage: string | null;
  created_at: string;
}

const day = (value: string) =>
  new Date(value).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
  });

const rpc = async (name: string, args: Record<string, unknown>) => {
  const { data, error } = await getSupabaseClient().rpc(name, args);
  if (error) throw new Error(error.message);
  return data;
};

const searchLeads = async (text: string): Promise<LeadOption[]> => {
  const term = searchTerm(text);
  if (term.length < 2) return [];
  const words = term.split(" ");
  let query = getSupabaseClient()
    .from("gula_clients_summary")
    .select("contact_id, first_name, last_name, phone, stage, created_at")
    .order("created_at", { ascending: false })
    .limit(8);
  // every word must appear in the name or the phone
  for (const word of words) {
    query = query.or(
      `first_name.ilike.*${word}*,last_name.ilike.*${word}*,phone.ilike.*${word}*`,
    );
  }
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => ({
    contact_id: Number(row.contact_id),
    name: [row.first_name, row.last_name].filter(Boolean).join(" ") || null,
    phone: row.phone ?? null,
    stage: row.stage ?? null,
    created_at: row.created_at,
  }));
};

const LeadButton = ({
  lead,
  busy,
  onPick,
}: {
  lead: LeadOption;
  busy: boolean;
  onPick: (lead: LeadOption) => void;
}) => (
  <button
    type="button"
    disabled={busy}
    onClick={() => onPick(lead)}
    className="flex items-center justify-between gap-3 rounded-xl border bg-card px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
  >
    <span className="min-w-0">
      <span className="block truncate font-medium">
        {lead.name ?? "Sin nombre"}
      </span>
      <span className="block truncate text-xs text-muted-foreground">
        {[
          lead.phone,
          lead.stage ? labelOf(CLIENT_STAGES, lead.stage) : null,
          `entró el ${day(lead.created_at)}`,
        ]
          .filter(Boolean)
          .join(" · ")}
      </span>
    </span>
    <span className="shrink-0 text-xs font-medium text-lime-700 dark:text-lime-300">
      Es este
    </span>
  </button>
);

/**
 * "¿Quién es?": links an Instagram / Facebook conversation to its lead (the
 * matches by name first, or a search), or creates the lead with a name.
 */
export const SocialWhoIs = ({
  threadId,
  onDone,
  onClose,
}: {
  threadId: Identifier;
  onDone: () => void;
  /** Already linked (changing the lead): the panel can be closed */
  onClose?: () => void;
}) => {
  const notify = useNotify();
  const [text, setText] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [busy, setBusy] = useState(false);

  const candidates = useQuery({
    queryKey: ["gula-social-candidates", threadId],
    queryFn: async () =>
      ((await rpc("gula_social_candidates", {
        p_thread_id: Number(threadId),
      })) ?? []) as LeadOption[],
  });
  const results = useQuery({
    queryKey: ["gula-social-search", text],
    queryFn: () => searchLeads(text),
    enabled: searchTerm(text).length >= 2,
  });

  const run = async (action: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await action();
      notify(done, { type: "success" });
      onDone();
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "No se ha podido guardar",
        {
          type: "error",
        },
      );
    } finally {
      setBusy(false);
    }
  };

  const pick = (lead: LeadOption) =>
    run(
      () =>
        rpc("gula_social_link", {
          p_thread_id: Number(threadId),
          p_contact_id: lead.contact_id,
        }),
      `Conversación vinculada a ${lead.name ?? "el lead"}`,
    );

  const create = () =>
    run(
      () =>
        rpc("gula_social_new_lead", {
          p_thread_id: Number(threadId),
          p_first_name: firstName,
          p_last_name: lastName || null,
        }),
      `Lead nuevo: ${firstName}`,
    );

  const found = results.data ?? [];
  return (
    <div className="flex flex-col gap-3 rounded-3xl border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-500/40 dark:bg-amber-500/10">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold">¿Quién es?</p>
          <p className="text-xs text-muted-foreground">
            Vincula la conversación a su lead para que salga con su nombre, en
            su ficha y en el tablero.
          </p>
        </div>
        {onClose ? (
          <button type="button" onClick={onClose} aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {candidates.data?.length ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Por el nombre, puede ser:
          </p>
          {candidates.data.map((lead) => (
            <LeadButton
              key={lead.contact_id}
              lead={lead}
              busy={busy}
              onPick={pick}
            />
          ))}
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Buscar el lead por nombre o teléfono…"
            className="bg-card pl-8"
          />
        </div>
        {found.map((lead) => (
          <LeadButton
            key={lead.contact_id}
            lead={lead}
            busy={busy}
            onPick={pick}
          />
        ))}
        {searchTerm(text).length >= 2 && results.isFetched && !found.length ? (
          <p className="text-xs text-muted-foreground">
            No hay ningún lead con ese nombre.
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5 border-t border-amber-300/60 pt-3 dark:border-amber-500/30">
        <p className="text-xs font-medium text-muted-foreground">
          ¿No está? Créalo como lead nuevo:
        </p>
        <div className="flex flex-wrap gap-2">
          <Input
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            placeholder="Nombre"
            className="w-36 flex-1 bg-card"
          />
          <Input
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            placeholder="Apellidos (opcional)"
            className="w-44 flex-1 bg-card"
          />
          <Button
            size="sm"
            disabled={busy || firstName.trim().length < 2}
            onClick={create}
          >
            <UserPlus className="h-4 w-4" />
            Crear lead
          </Button>
        </div>
      </div>
    </div>
  );
};
